import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import { VariableSizeList as List } from 'react-window';
import { Skeleton } from '@mui/material';

import './desktop-brand-list-style.scss';
import useScroll from '@src/hooks/useScroll';
import useIsComDomain from '@src/hooks/useIsComDomain';
import useBrandList from '@src/hooks/useBrandList';

import TabBar from '@src/componentsNew/TabBar';
import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import TopButton from '@src/componentsNew/Buttons/TopButton/TopButton';

import { UI_COMPONENT } from '@queries';
import { uiComponent } from '@src/queries/__generated__/uiComponent';

import { isNew } from '@src/utils';
import { uiComponentVariables } from '@src/core/config';
import { goToUrl } from '@src/utils/urlCheck';
import { SKELETON_COLOR } from '@src/utils/constants';
import { getInvisibleBrandIds } from "@src/utils/constants";

const tabItems = [
  { name: 'Top Brands' },
  { name: 'Only At Maccaron' },
  { name: 'New Launch' },
  { name: 'Clean Beauty' },
];

const DesktopBrandList = () => {
  const history = useHistory();
  const isComDomain = useIsComDomain();
  const { isScrolled, scrollTop } = useScroll();
  const { groupedBrandList, fetchBrandListLoading } = useBrandList();

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedAlphabet, setSelectedAlphabet] = useState(null);

  const brandListRef = useRef(null);
  const [listHeight, setListHeight] = useState(window.innerHeight * 1.5);

  const [getTopBrandData, { data: topBrandData, loading: topBrandLoading }] = useLazyQuery<uiComponent>(UI_COMPONENT, {
    fetchPolicy: 'no-cache',
  });

  const [
    getOnlyAtMaccaronData,
    { data: onlyAtMaccaronData, loading: onlyAtMaccaronLoading },
  ] = useLazyQuery<uiComponent>(UI_COMPONENT, {
    fetchPolicy: 'no-cache',
  });

  const [getNewLaunchData, { data: newLaunchData, loading: newLaunchLoading }] = useLazyQuery<uiComponent>(
    UI_COMPONENT,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const [getCleanBeautyData, { data: cleanBeautyData, loading: cleanBeautyLoading }] = useLazyQuery<uiComponent>(
    UI_COMPONENT,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const activeTopBrands = topBrandData?.uicomponent?.items?.filter((item) => item.isActive) ?? [];
  const activeOnlyAtMaccaron = onlyAtMaccaronData?.uicomponent?.items?.filter((item) => item.isActive) ?? [];
  const activeNewLaunch = newLaunchData?.uicomponent?.items?.filter((item) => item.isActive) ?? [];
  const activeCleanBeauty = cleanBeautyData?.uicomponent?.items?.filter((item) => item.isActive) ?? [];

  useEffect(() => {
    getTopBrandData({
      variables: isComDomain ? uiComponentVariables.topBrandWebCom : uiComponentVariables.topBrandWeb,
    });

    const handleResize = () => {
      setListHeight(window.innerHeight * 1.5);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (selectedAlphabet !== null && brandListRef.current) {
      const index = Object.keys(groupedBrandList ?? {}).indexOf(selectedAlphabet);
      if (index !== -1) {
        const count = Object.keys(groupedBrandList).length;
        brandListRef.current.scrollToItem(Math.min(index, count - 1), 'start');
      }
    }
  }, [selectedAlphabet, groupedBrandList]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);

    if (newValue === 0 && !topBrandData) {
      getTopBrandData({
        variables: isComDomain ? uiComponentVariables.topBrandWebCom : uiComponentVariables.topBrandWeb,
      });
    }
    if (newValue === 1 && !onlyAtMaccaronData) {
      getOnlyAtMaccaronData({ variables: uiComponentVariables.onlyAtMaccaronWeb });
    }
    if (newValue === 2 && !newLaunchData) {
      getNewLaunchData({ variables: uiComponentVariables.newLaunchWeb });
    }
    if (newValue === 3 && !cleanBeautyData) {
      getCleanBeautyData({ variables: uiComponentVariables.cleanBeauty });
    }
  };

  const handleClickAlphabet = (e, alphabet: string) => {
    e.stopPropagation();

    setSelectedAlphabet(alphabet);
  };

  // Dynamically calculate the height of each item
  const getBrandListElementSize = useCallback(
    (index) => {
      const alphabet = Object.keys(groupedBrandList)[index];

      const alphabetHeaderHeight = 56; // Base height for the alphabet header
      const brandItemHeight = 48; // Height for each brand item
      const brandsCountStartWithAlphabet = groupedBrandList[alphabet]?.length || 0;
      const isLastItem = index === Object.keys(groupedBrandList).length - 1;
      const spacing = 56;

      return alphabetHeaderHeight + (brandsCountStartWithAlphabet / 4) * brandItemHeight + (isLastItem ? 0 : spacing);
    },
    [groupedBrandList],
  );

  // Render each brand item
  const renderBrandItem = ({ index, style }) => {
    const alphabet = Object.keys(groupedBrandList)[index];

    return (
      <div id={alphabet === '#' ? 'notAlphabetGroup' : alphabet} className="alphabet-sorted-brand-list" style={style}>
        <Typography variant="h5" component="h5">
          {alphabet}
        </Typography>

        <ul className="brand-item-wrapper">
          {groupedBrandList?.[alphabet].filter((item) => !getInvisibleBrandIds().includes(String(item.id)))
              .map((item) => (
            <li key={item?.name} className="brand-item" onClick={() => goToUrl(history, item?.url)}>
              <Typography variant="body1" component="span">
                {item?.name}
              </Typography>
              {isNew(item?.publication_date) && (
                <Typography variant="body2" component="span" style={{ color: '#EF6BFF' }}>
                  N
                </Typography>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div id="desktop-brand-list-container">
      <div className="desktop-brand-list-content-wrapper">
        {isComDomain ? (
          activeTopBrands?.length > 0 ? (
            <div className="com-top-brand-list-area">
              <Typography variant="h7" component="h4">
                Top Brands
              </Typography>

              <ul className="com-top-brand-list">
                {activeTopBrands.map((topBrand) => (
                  <li key={topBrand.id}>
                    <img
                      src={topBrand.imageWebUrl}
                      onClick={() => goToUrl(history, topBrand.link)}
                      alt="top-brand-image"
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null
        ) : (
          <>
            <TabBar tabItems={tabItems} selectedTab={selectedTab} onTabChange={handleTabChange} />

            <div className="selected-tab-items-wrapper">
              {(topBrandLoading || onlyAtMaccaronLoading || newLaunchLoading || cleanBeautyLoading) && (
                <div className="skeleton-wrapper">
                  {Array.from({ length: 18 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      width={200}
                      height={60}
                      style={{ backgroundColor: SKELETON_COLOR, borderRadius: 6 }}
                    />
                  ))}
                </div>
              )}
              {selectedTab === 0 && activeTopBrands?.length > 0 && (
                <ul className="tab-item-list">
                  {activeTopBrands.map((topBrand) => (
                    <li key={topBrand.id}>
                      <img
                        src={topBrand.imageWebUrl}
                        onClick={() => goToUrl(history, topBrand.link)}
                        alt="top-brand-image"
                      />
                    </li>
                  ))}
                </ul>
              )}
              {selectedTab === 1 && activeOnlyAtMaccaron?.length > 0 && (
                <ul className="tab-item-list">
                  {activeOnlyAtMaccaron.map((onlyAtMaccaron) => (
                    <li key={onlyAtMaccaron.id}>
                      <img
                        src={onlyAtMaccaron.imageWebUrl}
                        onClick={() => goToUrl(history, onlyAtMaccaron.link)}
                        alt="only-at-maccaron-image"
                      />
                    </li>
                  ))}
                </ul>
              )}
              {selectedTab === 2 && activeNewLaunch?.length > 0 && (
                <ul className="tab-item-list">
                  {activeNewLaunch.map((newLaunch) => (
                    <li key={newLaunch.id}>
                      <img
                        src={newLaunch.imageWebUrl}
                        onClick={() => goToUrl(history, newLaunch.link)}
                        alt="new-launch-image"
                      />
                    </li>
                  ))}
                </ul>
              )}
              {selectedTab === 3 && activeCleanBeauty?.length > 0 && (
                <ul className="tab-item-list">
                  {activeCleanBeauty.map((cleanBeauty) => (
                    <li key={cleanBeauty.id}>
                      <img
                        src={cleanBeauty.imageWebUrl}
                        onClick={() => goToUrl(history, cleanBeauty.link)}
                        alt="clean-beauty-image"
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {fetchBrandListLoading ? (
          <div className="brand-list-area">
            <Skeleton
              variant="rectangular"
              width="100%"
              height={42}
              style={{ backgroundColor: SKELETON_COLOR, borderRadius: 6 }}
            />

            <Skeleton
              variant="rectangular"
              width={35}
              height={35}
              style={{ backgroundColor: SKELETON_COLOR, borderRadius: 6 }}
            />

            <div className="skeleton-brand-item-wrapper">
              {Array.from({ length: 48 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={300}
                  height={35}
                  style={{ backgroundColor: SKELETON_COLOR, borderRadius: 6 }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="brand-list-area">
            <div className="navigation-alphabet-wrapper">
              {['#', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))].map(
                (alphabet) =>
                  groupedBrandList?.[alphabet] && (
                    <Button
                      key={alphabet}
                      size="large"
                      cName={`navigation-alphabet-btn ${selectedAlphabet === alphabet ? 'selected' : ''}`}
                      onClick={(e) => handleClickAlphabet(e, alphabet)}
                    >
                      {alphabet}
                    </Button>
                  ),
              )}
            </div>

            <div className="alphabet-sorted-brand-list-area">
              {/* BrandList */}
              <List
                className="test"
                ref={brandListRef}
                height={listHeight}
                itemCount={groupedBrandList ? Object.keys(groupedBrandList).length : 0} // Number of alphabet groups
                itemSize={getBrandListElementSize} // Function to get the size of each item
                width="100%"
              >
                {renderBrandItem}
              </List>
            </div>
          </div>
        )}

        {isScrolled && <TopButton isMobile={false} onClick={scrollTop} />}
      </div>
    </div>
  );
};

export default DesktopBrandList;
